import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { useMemo, type CSSProperties } from 'react';
import { uploadContentImage } from '@/utils/uploadContentImage';
import styles from './RichTextEditor.module.scss';

interface RichTextEditorProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  height?: number;
  onUploadError?: (message: string) => void;
}

// build-classic and ckeditor5-react ship mismatched Editor types across minor versions.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Editor = ClassicEditor as any;

type FileLoader = {
  file: Promise<File | null>;
};

function createUploadAdapterPlugin(onUploadError?: (message: string) => void) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function UploadAdapterPlugin(editor: any) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: FileLoader) => ({
      upload: async () => {
        const file = await loader.file;
        if (!file) {
          const message = 'No image selected.';
          onUploadError?.(message);
          throw new Error(message);
        }

        try {
          const url = await uploadContentImage(file);
          return { default: url };
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Image upload failed.';
          onUploadError?.(message);
          throw error;
        }
      },
      abort: () => undefined,
    });
  };
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
  height = 180,
  onUploadError,
}: RichTextEditorProps) {
  const editorConfig = useMemo(
    () => ({
      placeholder,
      extraPlugins: [createUploadAdapterPlugin(onUploadError)],
      toolbar: [
        'heading',
        '|',
        'bold',
        'italic',
        'link',
        'bulletedList',
        'numberedList',
        '|',
        'blockQuote',
        'uploadImage',
        '|',
        'undo',
        'redo',
      ],
    }),
    [onUploadError, placeholder],
  );

  return (
    <div
      className={styles.editor}
      style={
        {
          '--rte-min-height': `${Math.max(120, height)}px`,
          '--rte-max-height': `${Math.max(240, height + 80)}px`,
        } as CSSProperties
      }
    >
      <CKEditor
        editor={Editor}
        data={value ?? ''}
        disabled={disabled}
        config={editorConfig}
        onChange={(_event, editor) => {
          onChange(editor.getData());
        }}
      />
    </div>
  );
}
